import { useState } from "react";
import { useGetList, useNotify, useTranslate } from "ra-core";
import { RefreshCw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { Contact, OutreachList } from "../types";
import { useTriggerAgentAction } from "./useTriggerAgentAction";

const DOOR_OPTIONS = [
  { value: "door_0_google_signal", label: "Door 0 — Google signal" },
  { value: "door_1_client_deficit", label: "Door 1 — Client deficit" },
  { value: "door_2_delivery_overwhelm", label: "Door 2 — Delivery overwhelm" },
  { value: "door_3_lack_of_control", label: "Door 3 — Lack of control" },
];

/**
 * PRD backlog item 5, stage 1: buttons, not a chatbox. Calls the
 * trigger-agent-action Edge Function (Martha AI BDR/docs/
 * agent-crm-integration-decisions.md Q4) — every click is logged to
 * mentorax.agent_runs regardless of whether the downstream target (Marco's
 * single-contact webhook, Martha's 7th Hermes route) is configured yet.
 * Neither exists yet as of 2026-09-04, so a click here will succeed at
 * logging but report "not configured yet" until those two follow-ups land.
 */
export const ContactAgentActions = ({ contact }: { contact: Contact }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { mutate: trigger, isPending } = useTriggerAgentAction();
  const [nudgeOpen, setNudgeOpen] = useState(false);

  const handleRescan = () => {
    trigger(
      {
        action: "rescan_contact",
        contact_id: Number(contact.id),
        company_id: contact.company_id ? Number(contact.company_id) : null,
      },
      {
        onSuccess: (result) => {
          notify(
            result.ok
              ? "Re-scan requested — Marco will pick this up."
              : (result.error ?? "Logged, but not dispatched yet."),
            { type: result.ok ? "success" : "warning" },
          );
        },
        onError: (error) => notify(String(error), { type: "error" }),
      },
    );
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={handleRescan}
        disabled={isPending}
      >
        <RefreshCw className="h-4 w-4" />
        {translate("resources.contacts.action.rescan_with_marco", {
          _: "Re-scan with Marco",
        })}
      </Button>
      <Button size="sm" variant="outline" onClick={() => setNudgeOpen(true)}>
        <Send className="h-4 w-4" />
        {translate("resources.contacts.action.nudge_toward_offer", {
          _: "Nudge toward offer",
        })}
      </Button>
      {nudgeOpen && (
        <NudgeTowardOfferDialog
          contact={contact}
          open={nudgeOpen}
          onClose={() => setNudgeOpen(false)}
        />
      )}
    </div>
  );
};

const NudgeTowardOfferDialog = ({
  contact,
  open,
  onClose,
}: {
  contact: Contact;
  open: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { mutate: trigger, isPending } = useTriggerAgentAction();
  const [targetDoor, setTargetDoor] = useState<string | undefined>();
  const [targetListId, setTargetListId] = useState<string | undefined>();

  const { data: lists } = useGetList<OutreachList>("outreach_lists", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const handleSubmit = () => {
    trigger(
      {
        action: "nudge_toward_offer",
        contact_id: Number(contact.id),
        target_door: targetDoor ?? null,
        target_list_id: targetListId ? Number(targetListId) : null,
      },
      {
        onSuccess: (result) => {
          notify(
            result.ok
              ? "Nudge sent — Martha will pick this up."
              : (result.error ?? "Logged, but not dispatched yet."),
            { type: result.ok ? "success" : "warning" },
          );
          onClose();
        },
        onError: (error) => notify(String(error), { type: "error" }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("resources.contacts.action.nudge_toward_offer_title", {
              _: "Nudge Martha toward an offer",
            })}
          </DialogTitle>
          <DialogDescription>
            {translate(
              "resources.contacts.action.nudge_toward_offer_description",
              {
                _: "Pick a door and/or a list — at least one, so Martha knows which direction to push this lead.",
              },
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Select value={targetDoor} onValueChange={setTargetDoor}>
            <SelectTrigger>
              <SelectValue placeholder="Target door (optional)" />
            </SelectTrigger>
            <SelectContent>
              {DOOR_OPTIONS.map((door) => (
                <SelectItem key={door.value} value={door.value}>
                  {door.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={targetListId} onValueChange={setTargetListId}>
            <SelectTrigger>
              <SelectValue placeholder="Target list (optional)" />
            </SelectTrigger>
            <SelectContent>
              {lists?.map((list) => (
                <SelectItem key={list.id} value={String(list.id)}>
                  {list.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending || (!targetDoor && !targetListId)}
          >
            {translate("resources.contacts.action.send_nudge", {
              _: "Send",
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
